import './HeroPointSpendView.css';
import { DispatchCardDismiss } from './DispatchCardDismiss';

/**
 * V9.2.5 (2026-06-06) — HeroPointSpendView.
 *
 * Pure-presentation render of a `force_hero_point_spend` dispatch
 * action. Player explicitly chose to expend HP per Ch 9 canonical
 * rules. Shows balance change (X → Y), purpose chip, and (for
 * alter_qr) the target QR.
 *
 * Schema 1-to-1 with the V9.1.b tool args at routes/dispatch.py:597-659:
 *   - current_balance (int ≥0, required): pre-spend balance.
 *     Canonical starting balances: Rookie 3 / Agent 8 / '00' 12
 *     (corrected in V8 Phase 4.A from prior 5/15/25 anchor).
 *   - points_spent (int ≥1, required)
 *   - purpose: enum {alter_qr, reroll, activate_ability, save_pc,
 *     qr1_award_claim}
 *   - qr_target (int|null): only valid if purpose=alter_qr (1-4).
 *   - rationale: GM voice-line ≤30 words (optional).
 *
 * Surfaces both the SETUP (intent to spend) and the resulting balance
 * delta — pure math, no engine call needed.
 */

export type HeroPointPurpose =
  | 'alter_qr'
  | 'reroll'
  | 'activate_ability'
  | 'save_pc'
  | 'qr1_award_claim';

export interface HeroPointSpendViewProps {
  currentBalance: number;
  pointsSpent: number;
  purpose: HeroPointPurpose;
  /** Required when purpose=alter_qr; ignored otherwise. */
  qrTarget?: number | null;
  rationale?: string;
  /** V9.2.x.3: when provided, renders a ✕ dismiss button (top-right). */
  onDismiss?: () => void;
  testidPrefix?: string;
}

const PURPOSE_LABEL: Record<HeroPointPurpose, string> = {
  alter_qr: 'Alter QR',
  reroll: 'Reroll',
  activate_ability: 'Activate Ability',
  save_pc: 'Save PC',
  qr1_award_claim: 'QR1 Award',
};

const PURPOSE_DESCRIPTION: Record<HeroPointPurpose, string> = {
  alter_qr: 'Bump the QR result by points_spent levels.',
  reroll: 'Discard result and re-roll.',
  activate_ability: 'Power an automatic ability.',
  save_pc: 'Override a Killed result — Ch 9 save-the-PC rule.',
  qr1_award_claim: 'Claim the QR1 bonus award.',
};

export function HeroPointSpendView({
  currentBalance,
  pointsSpent,
  purpose,
  qrTarget,
  rationale,
  onDismiss,
  testidPrefix = 'br-hp-spend',
}: HeroPointSpendViewProps) {
  const newBalance = Math.max(0, currentBalance - pointsSpent);
  const overdraw = pointsSpent > currentBalance;
  const showQrTarget = purpose === 'alter_qr' && typeof qrTarget === 'number';

  return (
    <aside
      className="br-hp-spend"
      data-testid={testidPrefix}
      data-purpose={purpose}
      data-overdraw={overdraw ? 'true' : 'false'}
    >
      <DispatchCardDismiss onDismiss={onDismiss} testidPrefix={testidPrefix} />
      <header className="br-hp-spend-header">
        <span className="br-hp-spend-label">Hero Point Spend</span>
        <span
          className="br-hp-spend-purpose-chip"
          data-testid={`${testidPrefix}-purpose-chip`}
          data-purpose-id={purpose}
        >
          {PURPOSE_LABEL[purpose]}
        </span>
      </header>

      <div
        className="br-hp-spend-balance-row"
        data-testid={`${testidPrefix}-balance-row`}
      >
        <span className="br-hp-spend-balance-cell br-label">
          <span className="br-hp-spend-balance-label">Was</span>
          <span
            className="br-hp-spend-balance-value"
            data-testid={`${testidPrefix}-current-balance`}
          >
            {currentBalance}
          </span>
        </span>
        <span
          className="br-hp-spend-arrow"
          data-testid={`${testidPrefix}-arrow`}
          aria-hidden="true"
        >
          →
        </span>
        <span className="br-hp-spend-balance-cell br-label">
          <span className="br-hp-spend-balance-label">Now</span>
          <span
            className="br-hp-spend-balance-value"
            data-testid={`${testidPrefix}-new-balance`}
          >
            {newBalance}
          </span>
        </span>
        <span
          className="br-hp-spend-delta br-label"
          data-testid={`${testidPrefix}-delta`}
        >
          −{pointsSpent} HP
        </span>
      </div>

      {overdraw && (
        <p
          className="br-hp-spend-overdraw-warn"
          data-testid={`${testidPrefix}-overdraw-warn`}
        >
          Insufficient Hero Points — spend exceeds current balance.
        </p>
      )}

      <p
        className="br-hp-spend-description"
        data-testid={`${testidPrefix}-description`}
      >
        {PURPOSE_DESCRIPTION[purpose]}
      </p>

      {showQrTarget && (
        <p
          className="br-hp-spend-qr-target"
          data-testid={`${testidPrefix}-qr-target`}
          data-qr-target={qrTarget}
        >
          Target QR after spend: <span className="br-label">QR {qrTarget}</span>
        </p>
      )}

      {rationale !== undefined && rationale.length > 0 && (
        <p
          className="br-hp-spend-rationale"
          data-testid={`${testidPrefix}-rationale`}
        >
          {rationale}
        </p>
      )}
    </aside>
  );
}
